import { createTheme } from '@mui/material/styles';

// Tema del PORTAL PARTNER: el MISMO lenguaje visual WAI que el portal cliente
// V2 (azul de marca #1976D2, ver src/theme.ts + CustomerNavbarV2) y el
// employee-portal (texto slate, sombras suaves en capas, radios generosos,
// fondo gris muy claro — ver ../employee-portal/src/theme.ts). La estructura
// comercial (hero con métricas, tarjetas grandes, onboarding en 3 pasos, chip
// de bolsa mayorista, footer de facturación) se conserva; solo cambia la piel.
// Se aplica con un ThemeProvider anidado solo bajo /partner/*.

const WAI_BLUE = '#1976D2';        // primario de marca (portal cliente V2)
const WAI_BLUE_DARK = '#1565C0';
const WAI_BLUE_LIGHT = '#42A5F5';
const SLATE_900 = '#0F172A';       // texto principal (employee-portal)
const SLATE_500 = '#64748B';
const SLATE_200 = '#E5E8F0';
const BG = '#F6F7FB';              // gris muy claro (employee-portal)

// Sombras suaves en capas (slate neutro, sin tinte de marca) — employee-portal.
const SOFT_SHADOW = '0 1px 2px rgba(15,23,42,0.04), 0 10px 28px rgba(15,23,42,0.05)';

export const partnerTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: WAI_BLUE,
      light: WAI_BLUE_LIGHT,
      dark: WAI_BLUE_DARK,
      contrastText: '#fff',
    },
    secondary: {
      main: '#0288D1', // azul cielo complementario, dentro de la familia WAI
      light: '#4FC3F7',
      dark: '#01579B',
      contrastText: '#fff',
    },
    background: {
      default: BG,
      paper: '#FFFFFF',
    },
    text: {
      primary: SLATE_900,
      secondary: SLATE_500,
    },
    divider: SLATE_200,
    success: { main: '#16A34A' },
    warning: { main: '#D97706' },
    error: { main: '#DC2626' },
    info: { main: WAI_BLUE },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: { fontWeight: 800, letterSpacing: '-0.03em' },
    h4: { fontWeight: 800, letterSpacing: '-0.025em' },
    h5: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none' as const, fontWeight: 600 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
        },
        containedPrimary: {
          boxShadow: '0 1px 2px rgba(25,118,210,0.30), 0 6px 16px rgba(25,118,210,0.22)',
          '&:hover': { boxShadow: '0 2px 4px rgba(25,118,210,0.32), 0 10px 24px rgba(25,118,210,0.28)' },
        },
        outlined: { borderColor: SLATE_200 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: `1px solid ${SLATE_200}`,
          boxShadow: SOFT_SHADOW,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 18 },
        outlined: { borderColor: SLATE_200 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: SLATE_200 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C7CDDB' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
        outlined: { borderColor: SLATE_200 },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
  },
});

// Degradado sutil del azul WAI para el hero (dashboard y login).
export const partnerHeroGradient =
  'linear-gradient(130deg, #0D47A1 0%, #1565C0 55%, #1E88E5 100%)';

// Fondo del item de navegación activo — azul WAI al 8% (antes terracota).
export const partnerNavActiveBg = 'rgba(25, 118, 210, 0.08)';
